import { useEffect, useState } from 'react';
import { scheduleAPI } from '../services/api';
import { cn } from '../utils/cn';

/**
 * One campaign's delivery record: who was targeted, what went out, what arrived.
 *
 * The team could not answer this during the meeting. Analytics showed totals
 * across every campaign, and the per-run rows were filed under an id that
 * changed every run, so there was no question anyone could ask that returned
 * one campaign's record. Naa could see that she and D'Angelo had received a
 * campaign and that roughly 25 others had not, and nothing could say why.
 *
 * The number that matters most is the gap between targeted and reached, so
 * that is the one stated first and in words, not left to be inferred from
 * two figures side by side.
 */

const STATUS_STYLES = {
  delivered: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  sent:      'bg-blue-50 text-blue-700 ring-blue-200',
  failed:    'bg-rose-50 text-rose-700 ring-rose-200',
  rejected:  'bg-rose-50 text-rose-700 ring-rose-200',
};

const STATUS_WORDS = {
  delivered: 'Delivered',
  sent: 'Sent, not yet confirmed',
  failed: 'Failed',
  rejected: 'Rejected',
};

function maskPhone(phone) {
  if (!phone || phone.length < 7) return phone || '—';
  return `${phone.slice(0, 6)}****${phone.slice(-3)}`;
}

function Figure({ label, value, tone = 'default' }) {
  return (
    <div>
      <div className={cn(
        'text-2xl font-semibold tabular-nums',
        tone === 'bad' && value > 0 ? 'text-rose-600' : 'text-gray-900',
      )}>
        {value}
      </div>
      <div className="mt-0.5 text-xs text-gray-500">{label}</div>
    </div>
  );
}

export default function DeliveryRecord({ programId, onClose }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    scheduleAPI.deliveries(programId)
      .then(d => { if (live) { setData(d); setError(null); } })
      .catch(e => {
        if (live) setError(e?.response?.data?.detail || 'Could not load the delivery record.');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [programId]);

  const missed = data ? data.targeted_count - data.reached_count : 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-gray-900/40 backdrop-blur-sm"
         onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {data?.program_name || 'Delivery record'}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Everyone this campaign has targeted, and what became of each message.
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg px-2.5 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
          >
            Close
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <p className="px-6 py-10 text-sm text-gray-400">Loading the record…</p>
          )}

          {error && !loading && (
            <p className="px-6 py-10 text-sm text-rose-600">{error}</p>
          )}

          {data && !loading && (
            <>
              <section className="px-6 py-5 border-b border-gray-100">
                <div className="grid grid-cols-4 gap-6">
                  <Figure label="Targeted"  value={data.targeted_count} />
                  <Figure label="Attempted" value={data.reached_count} />
                  <Figure label="Delivered" value={data.delivered_count} />
                  <Figure label="Failed"    value={data.failed_count} tone="bad" />
                </div>

                {/* The gap is the question people actually have. */}
                {missed > 0 && (
                  <p className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                    {missed} of {data.targeted_count} targeted {missed === 1 ? 'person has' : 'people have'}
                    {' '}not been attempted yet. That usually means the campaign has not run for them,
                    rather than that the message failed.
                  </p>
                )}
              </section>

              {data.never_attempted?.length > 0 && (
                <section className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Never attempted
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 break-words">
                    {data.never_attempted.map(maskPhone).join(', ')}
                  </p>
                </section>
              )}

              <section className="px-6 py-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Per recipient
                </h3>
                {data.recipients.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">
                    Nothing has been sent for this campaign yet.
                  </p>
                ) : (
                  <table className="mt-3 w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400">
                        <th className="pb-2 font-medium">Recipient</th>
                        <th className="pb-2 font-medium">Channel</th>
                        <th className="pb-2 font-medium">Outcome</th>
                        <th className="pb-2 font-medium text-right">Attempts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.recipients.map(r => (
                        <tr key={r.phone_number} className="align-top">
                          <td className="py-2.5 tabular-nums text-gray-900">
                            {maskPhone(r.phone_number)}
                          </td>
                          <td className="py-2.5 text-gray-500">{r.channel || '—'}</td>
                          <td className="py-2.5">
                            <span className={cn(
                              'inline-flex rounded-full px-2 py-0.5 text-xs ring-1 ring-inset',
                              STATUS_STYLES[r.status] || 'bg-gray-50 text-gray-600 ring-gray-200',
                            )}>
                              {STATUS_WORDS[r.status] || r.status}
                            </span>
                            {/* The provider's own words, not a generic failure. */}
                            {r.error && (
                              <p className="mt-1 text-xs text-rose-600">{r.error}</p>
                            )}
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-gray-500">
                            {r.attempts}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {data.runs?.length > 0 && (
                <section className="px-6 py-4 border-t border-gray-100">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Runs
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {data.runs.map(run => (
                      <li key={run.run_id} className="flex items-baseline justify-between gap-4 text-sm">
                        <span className="text-gray-600">
                          {run.scheduled_at
                            ? new Date(run.scheduled_at).toLocaleString()
                            : 'Not scheduled'}
                        </span>
                        <span className="text-gray-500">
                          {run.status.replace(/_/g, ' ')}
                          {run.recipients_count != null && ` · ${run.recipients_count} recipients`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
